<script lang="ts">
    import Chart from 'chart.js/auto'
    import ChartDataLabels from 'chartjs-plugin-datalabels'
    import { onMount } from 'svelte/internal'
    import { InvoicesRepository } from '../../modules/posApi/invoices/invoices.repository'
    import { InvoicesService } from '../../modules/posApi/invoices/invoices.service'
    import type { Invoice } from '../../modules/posApi/invoices/invoice'
    import type { Service } from '../../modules/posApi/services/service'
    import { ServicesRepository } from '../../modules/posApi/services/services.repository'
    import { ServicesService } from '../../modules/posApi/services/services.service'
    import DateSelect from '../_common/date-select.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import InlineSelect from '../_common/inline-select.svelte'
    import Zact from '../_common/z/zact.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zframe from '../_common/z/zframe.svelte'

    const rInvoices = new InvoicesRepository()
    const sInvoices = new InvoicesService(rInvoices)

    const rServices = new ServicesRepository()
    const sServives = new ServicesService(rServices)

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()

    let containerRanking: HTMLCanvasElement
    let containerCategoria: HTMLCanvasElement
    let containerRadar: HTMLCanvasElement
    let containerPrices: HTMLCanvasElement
    let containerQuantities: HTMLCanvasElement
    let containerHeatmap: HTMLCanvasElement
    let show

    let charts: Chart[] = []

    // altura dos rankings cresce com a quantidade de itens, para não espremer as barras
    let rankingHeight = 380
    let pricesHeight = 320

    type Item = { label: string; total: number; quantity: number; invoices: number }

    const MUTED = '#666'
    const GRID = '#555'
    const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
    const PALETTE = ['#008FFB', '#379E75', '#FEB019', '#775DD0', '#6179F3', '#D0483E']

    function dateChanged(event) {
        dates = event.detail
        getData()
    }

    async function getData() {
        try {
            show = true

            const invoices: Invoice[] = await sInvoices.getAll({
                from: dates.from,
                to: dates.to,
                payment: true,
                status: ['closed', 'subsidized', 'partial', 'open', 'credit'],
                service: true,
            })
            const services: Service[] = await sServives.getAll({ type: 'all', excluded: true })
            const categories = await window.api.pos.serviceCategories.getAll()

            const itemsByToggle = buildItems(invoices, services, categories, showBy)
            const itemsByCategory = showBy === 'category' ? itemsByToggle : buildItems(invoices, services, categories, 'category')
            const itemsByService = showBy === 'service' ? itemsByToggle : buildItems(invoices, services, categories, 'service')

            console.log('data', invoices, itemsByToggle)
            charts.forEach((c) => c.destroy())
            charts = []

            generateRanking(itemsByToggle)
            generateCategoryShare(itemsByCategory)
            generateRadar(itemsByService)
            generatePrices(itemsByToggle)
            generateQuantities(itemsByToggle)
            generateHeatmap(invoices)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function buildItems(invoices: Invoice[], services: Service[], categories, mode: 'service' | 'category'): Item[] {
        let items: Item[] = []
        for (const invoice of invoices) {
            for (const service of invoice.services || []) {
                let label = ''
                if (mode === 'category') {
                    const serviceFound = services.find((el) => el.serviceId === service.serviceId)
                    label = categories.find((el) => el.serviceCategoryId === serviceFound?.serviceCategoryId)?.name || 'Não informado'
                } else {
                    label = services.find((el) => el.serviceId === service.serviceId)?.name || 'Não informado'
                }
                let item = items.find((el) => el.label === label)
                if (!item) {
                    item = { label, total: 0, quantity: 0, invoices: 0 }
                    items.push(item)
                }
                item.total += service.quantity * service.value
                item.quantity += service.quantity
                item.invoices += 1
            }
        }
        items.sort((a, b) => b.total - a.total)
        return items
    }

    // agrupa os itens menos representativos em "Outros" para manter gráficos de participação legíveis
    function topNWithOthers(items: Item[], n: number): Item[] {
        const top = items.slice(0, n)
        const rest = items.slice(n)
        if (rest.length === 0) return top
        const outros = rest.reduce<Item>(
            (acc, el) => ({ label: 'Outros', total: acc.total + el.total, quantity: acc.quantity + el.quantity, invoices: acc.invoices + el.invoices }),
            { label: 'Outros', total: 0, quantity: 0, invoices: 0 },
        )
        return [...top, outros]
    }

    function currency(val: number) {
        return window.configs.currency + Number(val || 0).toFixed(2)
    }

    function hexToRgba(hex: string, alpha: number) {
        const n = parseInt(hex.replace('#', ''), 16)
        return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
    }

    // ranking por valor: substitui o treemap do ApexCharts (Chart.js não tem esse tipo nativamente)
    async function generateRanking(items: Item[]) {
        const top = items.slice(0, 20)
        rankingHeight = Math.max(340, top.length * 28)

        const chart = new Chart(containerRanking, {
            type: 'bar',
            data: {
                labels: top.map((el) => el.label),
                datasets: [{ label: 'Valor arrecadado', data: top.map((el) => el.total), backgroundColor: top.map((_, i) => PALETTE[i % PALETTE.length]), borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 60 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => currency(ctx.parsed.x) } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => currency(val) },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateCategoryShare(itemsByCategory: Item[]) {
        const grouped = topNWithOthers(itemsByCategory, 6)
        const totalGeral = grouped.reduce((t, el) => t + el.total, 0)
        const colors = [...PALETTE, '#9AA0A6']

        const chart = new Chart(containerCategoria, {
            type: 'doughnut',
            data: {
                labels: grouped.map((el) => el.label),
                datasets: [
                    {
                        data: grouped.map((el) => (totalGeral > 0 ? Number(((100 / totalGeral) * el.total).toFixed(1)) : 0)),
                        backgroundColor: grouped.map((el, i) => (el.label === 'Outros' ? '#9AA0A6' : colors[i % colors.length])),
                        borderColor: 'transparent',
                        borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.label + ': ' + ctx.parsed.toFixed(1) + '%' } },
                    datalabels: { color: '#fff', font: { size: 11, weight: 'bold' }, formatter: (val) => (val >= 5 ? val.toFixed(1) + '%' : '') },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateRadar(itemsByService: Item[]) {
        const top = [...itemsByService].sort((a, b) => b.total - a.total).slice(0, 5)
        if (top.length === 0) return

        const normalize = (values: number[]) => {
            const max = Math.max(...values, 1)
            return values.map((v) => Number(((v / max) * 100).toFixed(1)))
        }

        const metrics = [
            { name: 'Ticket médio', color: '#008FFB', data: normalize(top.map((el) => (el.invoices === 0 ? 0 : el.total / el.invoices))) },
            { name: 'Quantidade vendida', color: '#FEB019', data: normalize(top.map((el) => el.quantity)) },
            { name: 'Preço unitário', color: '#379E75', data: normalize(top.map((el) => (el.quantity === 0 ? 0 : el.total / el.quantity))) },
        ]

        const chart = new Chart(containerRadar, {
            type: 'radar',
            data: {
                labels: top.map((el) => el.label),
                datasets: metrics.map((m) => ({
                    label: m.name,
                    data: m.data,
                    borderColor: m.color,
                    backgroundColor: hexToRgba(m.color, 0.15),
                    pointBackgroundColor: m.color,
                    pointRadius: 3,
                    borderWidth: 2,
                })),
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.dataset.label + ': ' + ctx.parsed.r.toFixed(0) + ' / 100' } },
                },
                scales: {
                    r: {
                        min: 0,
                        max: 100,
                        ticks: { display: false, backdropColor: 'transparent' },
                        grid: { color: GRID },
                        angleLines: { color: GRID },
                        pointLabels: { color: MUTED },
                    },
                },
            },
        })
        charts.push(chart)
    }

    async function generatePrices(items: Item[]) {
        const rows = items
            .filter((el) => el.quantity > 0)
            .sort((a, b) => b.total / b.quantity - a.total / a.quantity)
            .slice(0, 15)
        pricesHeight = Math.max(320, rows.length * 26)

        const chart = new Chart(containerPrices, {
            type: 'bar',
            data: {
                labels: rows.map((el) => el.label),
                datasets: [{ label: 'Preço médio', data: rows.map((el) => Number((el.total / el.quantity).toFixed(2))), backgroundColor: '#379E75', borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 60 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => currency(ctx.parsed.x) } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => currency(val) },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateQuantities(items: Item[]) {
        const rows = [...items].sort((a, b) => b.invoices - a.invoices).slice(0, 15)

        const chart = new Chart(containerQuantities, {
            type: 'bar',
            data: { labels: rows.map((el) => el.label), datasets: [{ label: 'Vendas', data: rows.map((el) => el.invoices), backgroundColor: '#775DD0', borderRadius: 4 }] },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED, maxRotation: 45, minRotation: 45 } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    function hourBucketLabel(index: number) {
        const start = index * 2
        const end = start + 2
        return String(start).padStart(2, '0') + 'h-' + String(end).padStart(2, '0') + 'h'
    }

    // heatmap substituído por bubble chart: Chart.js não possui matriz/heatmap nativamente.
    // conta por venda (não por unidade) — uma venda de 30 unidades não deve pesar como 30 vendas de 1 unidade.
    async function generateHeatmap(invoices: Invoice[]) {
        let matrix = WEEKDAYS.map(() => Array(12).fill(0))
        for (const invoice of invoices) {
            const date = new Date(invoice.createdAt)
            const weekday = date.getDay()
            const bucket = Math.floor(date.getHours() / 2)
            matrix[weekday][bucket] += 1
        }
        const maxQ = Math.max(1, ...matrix.flat())

        let points: { x: number; y: number; r: number; qtde: number }[] = []
        matrix.forEach((row, wi) => {
            row.forEach((qtde, hi) => {
                if (qtde > 0) points.push({ x: hi, y: wi, r: 4 + (qtde / maxQ) * 16, qtde })
            })
        })

        const chart = new Chart(containerHeatmap, {
            type: 'bubble',
            data: { datasets: [{ data: points, backgroundColor: hexToRgba('#008FFB', 0.65), borderColor: '#008FFB' }] },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const p = ctx.raw as { x: number; y: number; qtde: number }
                                return WEEKDAYS[p.y] + ' ' + hourBucketLabel(p.x) + ': ' + p.qtde + ' venda(s)'
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        min: -0.5,
                        max: 11.5,
                        afterBuildTicks: (axis) => {
                            axis.ticks = Array.from({ length: 12 }, (_, i) => ({ value: i }))
                        },
                        ticks: { color: MUTED, callback: (v) => hourBucketLabel(Number(v)) },
                        grid: { display: false },
                    },
                    y: {
                        min: -0.5,
                        max: 6.5,
                        reverse: true,
                        afterBuildTicks: (axis) => {
                            axis.ticks = Array.from({ length: 7 }, (_, i) => ({ value: i }))
                        },
                        ticks: { color: MUTED, callback: (v) => WEEKDAYS[Number(v)] || '' },
                        grid: { display: false },
                    },
                },
            },
        })
        charts.push(chart)
    }

    function evaluateError(error) {
        window.error(error)
    }

    onMount(async () => {
        await getData()
    })

    let showBy: 'service' | 'category' = 'service'
    let showOptions = []
    showOptions.push({ label: 'Produto / Serviço', value: 'service' })
    showOptions.push({ label: 'Categoria', value: 'category' })
</script>

<div class="base-dash sales-dash">
    <Zact>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <InlineSelect class="limit-w_400" label="Exibir por" bind:value={showBy} options={showOptions} on:change={getData} />
        <div class="spacer"></div>
    </Zact>

    <ZContainer class="h-space">
        <Zframe
            size={12}
            title="Ranking dos mais vendidos por valor arrecadado"
            headline="Ranking dos itens com maior valor arrecadado no período. Use o seletor 'Exibir por' para alternar entre produto/serviço e categoria."
        >
            <div class="chart-box" style="height:{rankingHeight}px">
                <canvas bind:this={containerRanking}></canvas>
            </div>
        </Zframe>

        <Zframe size={6} sizeM={12} title="Participação por categoria" headline="Percentual do valor total arrecadado que cada categoria de serviço representa.">
            <div class="chart-box chart-box_fill">
                <canvas bind:this={containerCategoria}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Comparativo entre os principais serviços"
            headline="Compara ticket médio, quantidade vendida e preço unitário dos 5 serviços com maior faturamento. Os valores são normalizados (0 a 100) para comparar métricas de escalas diferentes."
        >
            <div class="chart-box chart-box_tall">
                <canvas bind:this={containerRadar}></canvas>
            </div>
        </Zframe>

        <Zframe size={6} sizeM={12} title="Valor médio unitário praticado" headline="Ranking do preço médio praticado, somando o valor vendido e dividindo pelas unidades vendidas.">
            <div class="chart-box" style="height:{pricesHeight}px">
                <canvas bind:this={containerPrices}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Quantidade de vendas realizadas"
            headline="Ranking da quantidade de vendas no período. Mesmo que o mesmo item apareça várias vezes em uma venda, é contabilizada apenas uma vez por venda."
        >
            <div class="chart-box">
                <canvas bind:this={containerQuantities}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={12}
            title="Horários de maior movimento"
            headline="Quantidade de vendas por dia da semana e faixa de horário (bolhas maiores = mais vendas), com base no horário de fechamento de cada venda. Contado por venda, não por unidade — uma venda com muitos itens não deve pesar mais que várias vendas pequenas."
        >
            <div class="chart-box">
                <canvas bind:this={containerHeatmap}></canvas>
            </div>
        </Zframe>
    </ZContainer>
</div>
<LoadSpinner delay={300} {show} />

<style>
    .chart-box {
        position: relative;
        width: 100%;
        height: 320px;
    }
    .chart-box_tall {
        height: 360px;
    }
    .chart-box_fill {
        height: 100%;
        min-height: 320px;
    }
</style>
